import {
  Award,
  CheckCircle2,
  ChevronsUp,
  FileUp,
  Heart,
  type LucideIcon,
} from "lucide-react"
import { Trans, useTranslation } from "react-i18next"

import { formatRelativeTime } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import type { ActivityEvent, ActivityEventType } from "@/types/recent-activity"

/**
 * OKS-393 — A single Recent Activity row (`EventRow`).
 *
 * Generalises the OKS-36 notification-row anatomy — leading icon slot ·
 * title · relative `<time>` — for the activity feed. Deliberately a
 * SEPARATE component from `NotificationRow`: activity rows are
 * **display-only** (no unread dot, no click/navigation, no PATCH per
 * PRD/UX OQ), so coupling them to the interactive notification row would
 * risk OKS-36. The shared core is the `formatRelativeTime` helper
 * (`@/lib/relative-time`), which both surfaces import.
 *
 * Per-type rendering (UX Spec §2.3):
 *   - the avatar slot becomes a tinted circular chip with a per-type
 *     lucide icon (icon is `aria-hidden`; meaning is carried by the
 *     text copy so colour/icon is never the sole signal — §6);
 *   - the title is the event's first-person copy with the dynamic noun
 *     emphasised via `<Trans>` + `<strong>`;
 *   - `<time dateTime>` exposes the real ISO timestamp to AT while the
 *     visible label is the relative string.
 *
 * `variant` mirrors the notification row: `panel` (compact, profile
 * block) vs `page` (spacious, standalone view).
 */
interface EventRowProps {
  event: ActivityEvent
  variant?: "panel" | "page"
}

interface TypeStyle {
  icon: LucideIcon
  /** Tailwind classes for the icon chip (bg + foreground). */
  chip: string
}

// UX Spec §2.3 icon + chip-tint table. Tints use semantic tokens where
// available, falling back to fixed palette steps for amber/rose (no
// dedicated token), matching the badge accent-colour posture.
const TYPE_STYLES: Record<ActivityEventType, TypeStyle> = {
  mission_submitted: { icon: FileUp, chip: "bg-muted text-muted-foreground" },
  content_approved: {
    icon: CheckCircle2,
    chip: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
  },
  rank_up: { icon: ChevronsUp, chip: "bg-primary/10 text-primary" },
  badge_earned: {
    icon: Award,
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  },
  kudo_received: {
    icon: Heart,
    chip: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400",
  },
}

export function EventRow({ event, variant = "panel" }: EventRowProps) {
  const { t, i18n } = useTranslation()
  const { icon: Icon, chip } = TYPE_STYLES[event.type]

  return (
    <li
      data-testid={`event-row-${event.id}`}
      data-event-type={event.type}
      className={cn(
        "flex w-full items-start gap-3 border-b transition-colors last:border-b-0",
        variant === "panel"
          ? "px-4 py-3"
          : "hover:bg-muted/40 px-4 py-4 sm:px-6 sm:py-5",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full",
          chip,
        )}
      >
        <Icon className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm leading-snug">
          <EventTitle event={event} />
        </p>
        <time
          dateTime={event.occurred_at}
          className="text-muted-foreground/80 mt-1 text-[11px] tracking-wide uppercase"
        >
          {formatRelativeTime(
            event.occurred_at,
            t,
            i18n.language,
            "recent_activity.time",
          )}
        </time>
      </div>
    </li>
  )
}

/**
 * Resolves the first-person copy for an event, emphasising the dynamic
 * noun. The noun is escaped by React (no user-injected HTML risk) and
 * the surrounding sentence comes from the localised string so EN/FR word
 * order is preserved.
 */
function EventTitle({ event }: { event: ActivityEvent }) {
  const { t } = useTranslation()
  const { type, params } = event

  switch (type) {
    case "mission_submitted":
    case "content_approved":
      return (
        <Trans
          i18nKey={`recent_activity.events.${type}`}
          values={{ mission: params.mission ?? "" }}
          components={{ b: <strong className="font-semibold" /> }}
        />
      )
    case "rank_up":
      return (
        <Trans
          i18nKey="recent_activity.events.rank_up"
          values={{ rank: params.rank ?? "" }}
          components={{ b: <strong className="font-semibold" /> }}
        />
      )
    case "badge_earned": {
      // Milestone badges resolve their name via the shared badges
      // dictionary; custom badges carry a literal name.
      const badge = params.badge_i18n_key
        ? t(`badges.milestones.${params.badge_i18n_key}.name`)
        : (params.badge_name ?? "")
      return (
        <Trans
          i18nKey="recent_activity.events.badge_earned"
          values={{ badge }}
          components={{ b: <strong className="font-semibold" /> }}
        />
      )
    }
    case "kudo_received": {
      const from = params.from?.trim()
      // Missing/blank actor → privacy-safe fallback (UX Spec §3); never
      // surface the sender's email in the feed.
      if (!from)
        return <>{t("recent_activity.events.kudo_received_anonymous")}</>
      return (
        <Trans
          i18nKey="recent_activity.events.kudo_received"
          values={{ from }}
          components={{ b: <strong className="font-semibold" /> }}
        />
      )
    }
  }
}
